import { useState, useRef, useMemo } from 'react';

import type { SlideConfig, StepHelpers, StepConfig2, StepState } from '../StepWizard2';

interface UseSlideNavigationOptions {
    /** The currently active step config */
    activeStep: StepConfig2 | undefined;
    /** Current step's state (canProceed, data) */
    currentStepState: StepState;
    /** Step states setter — used for canProceed resets on slide transitions */
    setStepStates: React.Dispatch<React.SetStateAction<Record<string, StepState>>>;
    /** Global wizard data — passed to detection helpers for dynamic slide construction */
    wizardData: Record<string, any>;
    /** Wizard data setter — passed to detection helpers */
    setWizardData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
    /** Current step index in the wizard */
    currentStepIndex: number;
    /** Total number of steps */
    totalSteps: number;
    /** Whether this is the first step */
    isFirstStep: boolean;
    /** Whether this is the last step */
    isLastStep: boolean;
}

interface SlideNavigationResult {
    /** Whether the active step has slides */
    hasSlides: boolean;
    /** Current slide index (0 when no slides) */
    currentSlideIndex: number;
    /** Whether on the first slide */
    isFirstSlide: boolean;
    /** Whether on the last slide (true when no slides) */
    isLastSlide: boolean;
    /** The active slide config, or null if step has no slides */
    activeSlide: SlideConfig | null;
    /** Total number of slides, or undefined if step has no slides */
    totalSlides: number | undefined;
    /** Advance to next slide with canProceed reset. Returns true if handled. */
    handleSlideNext: () => boolean;
    /** Retreat to previous slide with canProceed reset. Returns true if handled. */
    handleSlidePrevious: () => boolean;
}

export default function useSlideNavigation({
    activeStep,
    currentStepState,
    setStepStates,
    wizardData,
    setWizardData,
    currentStepIndex,
    totalSteps,
    isFirstStep,
    isLastStep,
}: UseSlideNavigationOptions): SlideNavigationResult {
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const previousStepPathRef = useRef<string | undefined>(undefined);
    const previousStepIndexRef = useRef<number>(currentStepIndex);

    const detectedSlides = useMemo(() => {
        if (activeStep) {
            const detectionHelpers: StepHelpers = {
                setCanProceed: (canProceed: boolean) => {
                    setStepStates((prev) => ({
                        ...prev,
                        [activeStep.path]: { ...prev[activeStep.path], canProceed },
                    }));
                },
                canProceed: currentStepState.canProceed,
                setStepData: (data: any) => {
                    setStepStates((prev) => ({
                        ...prev,
                        [activeStep.path]: { ...prev[activeStep.path], data },
                    }));
                },
                getStepData: () => currentStepState.data,
                setWizardData: (key: string, value: any) => {
                    setWizardData((prev) => ({ ...prev, [key]: value }));
                },
                getWizardData: (key: string) => wizardData[key],
                getAllWizardData: () => wizardData,
                currentStepIndex,
                totalSteps,
                isFirstStep,
                isLastStep,
            };

            const contentResult = activeStep.content(detectionHelpers);
            if (Array.isArray(contentResult)) {
                return contentResult;
            }
        }
        return undefined;
    }, [
        activeStep,
        currentStepState,
        setStepStates,
        setWizardData,
        wizardData,
        currentStepIndex,
        totalSteps,
        isFirstStep,
        isLastStep,
    ]);

    const slideCount = detectedSlides?.length ?? 0;
    const hasSlides = slideCount > 0;

    // Resets slide index when entering a new step.
    // When navigating backwards, land on the last slide; otherwise land on the first.
    if (activeStep?.path !== previousStepPathRef.current) {
        const navigatedBackward = currentStepIndex < previousStepIndexRef.current;
        previousStepPathRef.current = activeStep?.path;
        previousStepIndexRef.current = currentStepIndex;
        if (hasSlides && activeStep && detectedSlides) {
            const targetSlideIndex = navigatedBackward ? detectedSlides.length - 1 : 0;
            setCurrentSlideIndex(targetSlideIndex);
            setStepStates((prev) => {
                const existing = prev[activeStep.path];
                return {
                    ...prev,
                    [activeStep.path]: {
                        data: existing?.data ?? activeStep.initialData ?? {},
                        isLoading: existing?.isLoading ?? false,
                        canProceed: detectedSlides[targetSlideIndex].validateOnMount || false,
                    },
                };
            });
        }
    }

    // Clamp slide index to safely handle case where index drifts out of bounds
    const effectiveSlideIndex = hasSlides ? Math.min(currentSlideIndex, Math.max(slideCount - 1, 0)) : 0;

    const isFirstSlideVal = effectiveSlideIndex === 0;
    const isLastSlideVal = hasSlides ? effectiveSlideIndex === slideCount - 1 : true;
    const activeSlide = hasSlides && detectedSlides ? detectedSlides[effectiveSlideIndex] : null;

    const handleSlideNext = (): boolean => {
        if (!hasSlides || isLastSlideVal || !detectedSlides || !activeStep?.path) {
            return false;
        }
        setCurrentSlideIndex(effectiveSlideIndex + 1);
        const nextSlide = detectedSlides[effectiveSlideIndex + 1];
        setStepStates((prev) => ({
            ...prev,
            [activeStep.path]: {
                ...prev[activeStep.path],
                canProceed: nextSlide?.validateOnMount || false,
            },
        }));
        return true;
    };

    const handleSlidePrevious = (): boolean => {
        if (!hasSlides || isFirstSlideVal || !detectedSlides || !activeStep?.path) {
            return false;
        }
        setCurrentSlideIndex(effectiveSlideIndex - 1);
        const prevSlide = detectedSlides[effectiveSlideIndex - 1];
        setStepStates((prev) => ({
            ...prev,
            [activeStep.path]: {
                ...prev[activeStep.path],
                canProceed: prevSlide?.validateOnMount || false,
            },
        }));
        return true;
    };

    return {
        hasSlides,
        currentSlideIndex: effectiveSlideIndex,
        isFirstSlide: isFirstSlideVal,
        isLastSlide: isLastSlideVal,
        activeSlide,
        totalSlides: hasSlides ? slideCount : undefined,
        handleSlideNext,
        handleSlidePrevious,
    };
}
